//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { getButtonStyle } from '@components/atoms/button/Button';
import {
  DefaultButtonStyles,
  FlatDefaultButtonStyles,
  FlatPrimaryButtonStyles,
  IButtonStateStyles,
  PrimaryButtonStyles,
  PrimaryLinkButtonStyles,
  SecondaryButtonStyles,
  TertiaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import {
  getStateHref,
  navigateToState,
} from '@react-bridge/wrappers/navigation';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

import { RoutableStateName } from 'app/top-level-states';
import Icon from '@components/atoms/icon/Icon';
import { UnisonColor, UnisonIconType } from '@bvengineering/unison-core';
import {
  ActionButtonSize,
  getActionButtonStyles,
} from '@components/atoms/action-button/ActionButton';

interface RouteAnchorProps<T extends RoutableStateName>
  extends Omit<React.ComponentPropsWithoutRef<'a'>, 'href'>,
    ITrackableComponent {
  state: Parameters<typeof navigateToState<T>>[0];
  stateParams?: Parameters<typeof navigateToState<T>>[1];
  stateOptions?: Parameters<typeof navigateToState<T>>[2];
  isDisabled?: boolean;
}

interface ActionButtonRouteAnchorProps<T extends RoutableStateName>
  extends RouteAnchorProps<T> {
  iconColor?: UnisonColor;
  iconName: UnisonIconType;
  label?: string;
  size?: ActionButtonSize;
}

/**
 * Anchor element targetting an SPA state
 */
export const RouteAnchor = <T extends RoutableStateName>({
  children,
  isDisabled,
  onClick,
  state,
  stateOptions,
  stateParams,
  target,
  ...restProps
}: RouteAnchorProps<T>) => {
  const href = getStateHref(state, stateParams, stateOptions);

  return (
    <Anchor
      {...restProps}
      href={isDisabled ? undefined : href}
      {...(target && { target })}
      {...(isDisabled && { 'aria-disabled': true, role: 'link' })}
      onClick={
        isDisabled
          ? undefined
          : (e) => {
              // Call consumer's onclick if available.
              onClick?.(e);

              // If consumer prevents default, don't navigate. When we want to open
              // with a different target, we cannot rely on the SPA handling
              if (!e.isDefaultPrevented() && !target) {
                e.preventDefault();
                void navigateToState(
                  state as RoutableStateName,
                  stateParams,
                  stateOptions,
                );
              }
            }
      }
    >
      {children}
    </Anchor>
  );
};

const Anchor = withTracking(styled.a``);

/**
 * Anchor element targetting an SPA state.
 * Pre-styled with Link-Primary coloring.
 */
export const PrimaryRouteAnchor = styled(RouteAnchor)<{ isDisabled?: boolean }>(
  ({ isDisabled, theme }) => css`
    ${Mixins.Text.LinkPrimary}
    ${isDisabled &&
    `
      && {
        &,
        &:hover {
          text-decoration: none;
          color: ${theme.colors.aliases.disabled};
          cursor: not-allowed;
        }
      }
    `}
  `,
);

const ButtonRouteAnchor = styled(RouteAnchor)<{
  isDisabled?: boolean;
  buttonStyles: IButtonStateStyles;
}>(
  ({ buttonStyles, isDisabled, theme }) => css`
    ${getButtonStyle({ theme, buttonStyles, isDisabled })}

    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${theme.fontWeights.semiBold};
    && {
      text-decoration: none;
      color: ${buttonStyles.standard.color};
    }

    &&:hover {
      color: ${buttonStyles.hover.color};
      text-decoration: none;
    }

    ${isDisabled &&
    `
      && {
      color: ${buttonStyles.disabled.color}; // needed because disabled color will be overridden in anchor element
      }
    `}
  `,
);

export const PrimaryButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={PrimaryButtonStyles} {...props} />;

export const DefaultButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={DefaultButtonStyles} {...props} />;

export const SecondaryButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={SecondaryButtonStyles} {...props} />;

export const FlatPrimaryButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={FlatPrimaryButtonStyles} {...props} />;

export const FlatDefaultButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={FlatDefaultButtonStyles} {...props} />;

export const TertiaryButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={TertiaryButtonStyles} {...props} />;

export const PrimaryLinkButtonStyleRouteAnchor = <T extends RoutableStateName>(
  props: RouteAnchorProps<T>,
) => <ButtonRouteAnchor buttonStyles={PrimaryLinkButtonStyles} {...props} />;

const ActionButtonRouteAnchor = styled(RouteAnchor)<{
  $iconColor?: UnisonColor;
  $size: ActionButtonSize;
}>(
  ({ $iconColor, $size, theme }) => css`
    ${getActionButtonStyles($size)}

    svg {
      color: ${$iconColor ?? theme.colors.gray7};
    }

    &:hover svg {
      color: ${$iconColor ?? theme.colors.gray9};
    }
  `,
);

export const ActionButtonStyleRouteAnchor = <T extends RoutableStateName>({
  iconColor,
  iconName,
  label,
  size = 'small',
  ...routeProps
}: ActionButtonRouteAnchorProps<T>) => (
  <ActionButtonRouteAnchor
    $iconColor={iconColor}
    $size={size}
    aria-label={label ?? iconName}
    title={label}
    {...routeProps}
  >
    <Icon name={iconName} />
  </ActionButtonRouteAnchor>
);
